import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Printer } from 'lucide-react';
import {
  printLabelBrowser,
  printLabelPrintNode,
  printReceiptBrowser,
  printReceiptPrintNode,
} from '@/lib/print.js';

const variantClasses = {
  primary: 'bg-primary text-white hover:opacity-95 border-transparent',
  secondary: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
  ghost: 'border border-slate-300 text-slate-700 hover:bg-slate-50',
};

const sizeClasses = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3 py-1.5 text-sm',
};

export function PrintButton({
  type,
  ticketId,
  hasPrintNode,
  label,
  className = '',
  size = 'md',
  variant = 'secondary',
  disabled = false,
}: {
  type: 'label' | 'receipt';
  ticketId: string;
  hasPrintNode: boolean;
  label?: string;
  className?: string;
  size?: 'sm' | 'md';
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const defaultLabel = type === 'label' ? 'Print Label' : 'Print Receipt';
  const displayLabel = label ?? defaultLabel;
  const fullWidth = className.includes('w-full');
  const base = `inline-flex items-center justify-center gap-1 rounded-xl font-semibold disabled:opacity-50 ${variantClasses[variant]} ${sizeClasses[size]}`;

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  async function run(action: 'printnode' | 'browser') {
    setBusy(true);
    setOpen(false);
    try {
      if (type === 'label') {
        if (action === 'printnode') await printLabelPrintNode(ticketId);
        else await printLabelBrowser(ticketId);
      } else if (action === 'printnode') await printReceiptPrintNode(ticketId);
      else await printReceiptBrowser(ticketId);
    } finally {
      setBusy(false);
    }
  }

  if (!hasPrintNode) {
    return (
      <button
        type="button"
        className={`${base} ${className}`}
        disabled={disabled || busy}
        onClick={() => void run('browser')}
      >
        <Printer className="h-4 w-4" />
        {busy ? 'Printing…' : displayLabel}
      </button>
    );
  }

  return (
    <div ref={rootRef} className={`relative inline-flex ${fullWidth ? 'w-full' : ''} ${className}`}>
      <button
        type="button"
        className={`${base} ${fullWidth ? 'flex-1' : ''} rounded-r-none border-r-0 pr-2`}
        disabled={disabled || busy}
        onClick={() => void run('printnode')}
      >
        <Printer className="h-4 w-4" />
        {busy ? 'Printing…' : displayLabel}
      </button>
      <button
        type="button"
        className={`${base} rounded-l-none px-1.5`}
        disabled={disabled || busy}
        aria-label={`${displayLabel} options`}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-1 min-w-[200px] rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-lg">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-slate-50"
            onClick={() => void run('printnode')}
          >
            <Printer className="h-4 w-4" />
            Print via PrintNode
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-slate-50"
            onClick={() => void run('browser')}
          >
            <Printer className="h-4 w-4" />
            Print in Browser
          </button>
        </div>
      ) : null}
    </div>
  );
}
